import React, { useState } from 'react';
import {
  AlertTriangle,
  Clock,
  Package,
  TrendingDown,
  Truck,
  Zap,
  CheckCircle,
  XCircle,
  Eye,
  Bell,
  Filter,
  Search,
  Calendar,
  MapPin,
  Building,
  ArrowRight,
  Target,
  Activity
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface OrderingAlert {
  id: string;
  type: 'low_stock' | 'lead_time' | 'seasonal_demand' | 'supplier_issue' | 'cost_optimization' | 'transfer_opportunity';
  severity: 'low' | 'medium' | 'high' | 'critical';
  medication: string;
  currentStock: number;
  reorderPoint: number;
  recommendedAction: string;
  estimatedImpact: string;
  leadTime: number;
  supplier: string;
  costSaving?: number;
  transferFrom?: string;
  createdAt: string;
  dueDate: string;
  status: 'new' | 'acknowledged' | 'action_taken' | 'dismissed';
}

const IntelligentOrderingAlerts: React.FC = () => {
  const { user } = useAuth();
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const mockAlerts: OrderingAlert[] = [
    {
      id: 'oa-001',
      type: 'low_stock',
      severity: 'critical',
      medication: 'Atorvastatin 20mg',
      currentStock: 15,
      reorderPoint: 50,
      recommendedAction: 'Order 500 units immediately',
      estimatedImpact: 'Stock out in 2 days without action',
      leadTime: 3,
      supplier: 'Sigma Healthcare',
      createdAt: format(new Date(Date.now() - 30 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
      status: 'new'
    },
    {
      id: 'oa-002',
      type: 'transfer_opportunity',
      severity: 'medium',
      medication: 'Metformin 500mg',
      currentStock: 25,
      reorderPoint: 75,
      recommendedAction: 'Transfer 100 units from MedFast Westfield',
      estimatedImpact: 'Save $150 vs new order, 1-day delivery',
      leadTime: 1,
      supplier: 'API',
      transferFrom: 'MedFast Westfield',
      costSaving: 150,
      createdAt: format(new Date(Date.now() - 45 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 3), 'yyyy-MM-dd'),
      status: 'new'
    },
    {
      id: 'oa-003',
      type: 'seasonal_demand',
      severity: 'high',
      medication: 'Flu Vaccine',
      currentStock: 50,
      reorderPoint: 100,
      recommendedAction: 'Increase order to 300 units for flu season',
      estimatedImpact: 'Meet 200% demand increase expected in 2 weeks',
      leadTime: 7,
      supplier: 'CSL Seqirus',
      createdAt: format(new Date(Date.now() - 60 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
      status: 'acknowledged'
    },
    {
      id: 'oa-004',
      type: 'cost_optimization',
      severity: 'medium',
      medication: 'Paracetamol 500mg',
      currentStock: 200,
      reorderPoint: 150,
      recommendedAction: 'Switch to generic supplier for 15% cost reduction',
      estimatedImpact: 'Save $200/month on this medication',
      leadTime: 5,
      supplier: 'Generic Plus',
      costSaving: 200,
      createdAt: format(new Date(Date.now() - 2 * 60 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
      status: 'new'
    },
    {
      id: 'oa-005',
      type: 'supplier_issue',
      severity: 'high',
      medication: 'Insulin Glargine',
      currentStock: 8,
      reorderPoint: 25,
      recommendedAction: 'Use alternative supplier due to Novo Nordisk delay',
      estimatedImpact: 'Avoid 5-day stockout, maintain patient care',
      leadTime: 2,
      supplier: 'Sanofi',
      createdAt: format(new Date(Date.now() - 90 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      status: 'new'
    },
    {
      id: 'oa-006',
      type: 'lead_time',
      severity: 'medium',
      medication: 'Amlodipine 5mg',
      currentStock: 45,
      reorderPoint: 40,
      recommendedAction: 'Order now due to extended lead time (7 days vs usual 3)',
      estimatedImpact: 'Prevent stockout during supplier transition',
      leadTime: 7,
      supplier: 'Pfizer',
      createdAt: format(new Date(Date.now() - 120 * 60 * 1000), 'yyyy-MM-dd HH:mm'),
      dueDate: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
      status: 'acknowledged'
    }
  ];

  const filteredAlerts = mockAlerts.filter(alert => {
    const matchesSearch = alert.medication.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || alert.type === filterType;
    const matchesSeverity = filterSeverity === 'all' || alert.severity === filterSeverity;
    const matchesStatus = filterStatus === 'all' || alert.status === filterStatus;
    return matchesSearch && matchesType && matchesSeverity && matchesStatus;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'low_stock': return <Package className="h-4 w-4" />;
      case 'lead_time': return <Clock className="h-4 w-4" />;
      case 'seasonal_demand': return <TrendingDown className="h-4 w-4" />;
      case 'supplier_issue': return <Building className="h-4 w-4" />;
      case 'cost_optimization': return <Target className="h-4 w-4" />;
      case 'transfer_opportunity': return <Truck className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'low_stock': return 'bg-red-100 text-red-800 border-red-200';
      case 'lead_time': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'seasonal_demand': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'supplier_issue': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cost_optimization': return 'bg-green-100 text-green-800 border-green-200';
      case 'transfer_opportunity': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'action_taken': return 'bg-green-100 text-green-800';
      case 'dismissed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTakeAction = (alertId: string) => {
    console.log(`Taking action for alert ${alertId}`);
  };

  const handleAcknowledge = (alertId: string) => {
    console.log(`Acknowledging alert ${alertId}`);
  };

  const handleDismiss = (alertId: string) => {
    console.log(`Dismissing alert ${alertId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Intelligent Ordering Alerts ⚡
          </h1>
          <p className="text-gray-600 mt-2">AI-powered ordering recommendations and smart inventory alerts for {user?.branch}.</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Zap className="h-4 w-4" />
            <span>Run Analysis</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Eye className="h-4 w-4" />
            <span>View Settings</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-red-100 text-red-600">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Critical Alerts</p>
            <p className="text-2xl font-bold text-gray-900">{mockAlerts.filter(a => a.severity === 'critical').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Cost Savings Available</p>
            <p className="text-2xl font-bold text-gray-900">${mockAlerts.reduce((sum, a) => sum + (a.costSaving || 0), 0)}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
            <Bell className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">New Alerts</p>
            <p className="text-2xl font-bold text-gray-900">{mockAlerts.filter(a => a.status === 'new').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-green-100 text-green-600">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Actions Taken Today</p>
            <p className="text-2xl font-bold text-gray-900">{mockAlerts.filter(a => a.status === 'action_taken').length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search medications, suppliers..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="low_stock">Low Stock</option>
            <option value="lead_time">Lead Time</option>
            <option value="seasonal_demand">Seasonal Demand</option>
            <option value="supplier_issue">Supplier Issue</option>
            <option value="cost_optimization">Cost Optimization</option>
            <option value="transfer_opportunity">Transfer Opportunity</option>
          </select>

          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="action_taken">Action Taken</option>
            <option value="dismissed">Dismissed</option>
          </select>

          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply</span>
          </button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Smart Ordering Recommendations</h3>
        <div className="space-y-4">
          {filteredAlerts.map((alert) => (
            <div key={alert.id} className="p-6 border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-lg border ${getTypeColor(alert.type)}`}>
                    {getTypeIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{alert.medication}</h4>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(alert.status)}`}>
                        {alert.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{alert.recommendedAction}</p>
                    <p className="text-sm text-gray-600 mb-3">{alert.estimatedImpact}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Current Stock:</span>
                        <span className="ml-2 font-medium text-gray-900">{alert.currentStock} units</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Reorder Point:</span>
                        <span className="ml-2 font-medium text-gray-900">{alert.reorderPoint} units</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Lead Time:</span>
                        <span className="ml-2 font-medium text-gray-900">{alert.leadTime} days</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Supplier:</span>
                        <span className="ml-2 font-medium text-gray-900">{alert.supplier}</span>
                      </div>
                    </div>

                    {alert.costSaving && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Target className="h-4 w-4 text-green-600" />
                          <span className="text-green-800 font-medium">Potential Savings: ${alert.costSaving}</span>
                        </div>
                      </div>
                    )}

                    {alert.transferFrom && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Truck className="h-4 w-4 text-purple-600" />
                          <span className="text-purple-800 font-medium">Transfer Available from: {alert.transferFrom}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>Due: {format(new Date(alert.dueDate), 'dd/MM/yyyy')}</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-500">
                  Created: {format(new Date(alert.createdAt), 'dd/MM/yyyy HH:mm')}
                </div>
                <div className="flex space-x-2">
                  {alert.status === 'new' && (
                    <>
                      <button 
                        onClick={() => handleAcknowledge(alert.id)}
                        className="px-4 py-2 text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 text-sm font-medium"
                      >
                        Acknowledge
                      </button>
                      <button 
                        onClick={() => handleTakeAction(alert.id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium flex items-center space-x-1"
                      >
                        <span>Take Action</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {alert.status === 'acknowledged' && (
                    <button 
                      onClick={() => handleTakeAction(alert.id)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium flex items-center space-x-1"
                    >
                      <span>Take Action</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                  <button 
                    onClick={() => handleDismiss(alert.id)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntelligentOrderingAlerts;
